import { useState, type FormEvent, type ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';

import { t } from '../../i18n';
import { toAuthFailure } from '../../lib/auth';
import { Alert, AlertDescription } from '../../ui/alert';
import { Button } from '../../ui/button';
import { Field, FieldLabel } from '../../ui/field';
import { Input } from '../../ui/input';
import { useAuthSession } from '../auth/AuthProvider';
import { AuthFrame } from './AuthFrame';
import { AUTH_ROUTE_PATHS } from './paths';

export function ResetPassword(): ReactNode {
  const { session } = useAuthSession();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await session.sendPasswordReset(email);
      setSentEmail(email);
      setSent(true);
    } catch (cause) {
      // `invalid-credentials` is `toAuthFailure`'s enumeration-safe bucket
      // (src/lib/auth.ts) -- it folds `auth/user-not-found` in with a
      // malformed or disabled address, so a password-reset form is just as
      // much an account-enumeration oracle as a login form is, and reaching
      // the sent state for all of them must not depend on the Firebase
      // project's own "email enumeration protection" setting. A failure
      // outside that bucket (network, rate limit) reveals nothing about the
      // address and still gets the generic error.
      if (toAuthFailure(cause) === 'invalid-credentials') {
        setSentEmail(email);
        setSent(true);
      } else {
        setError(t('auth.resetPassword.error'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend(): Promise<void> {
    setResendError(null);
    setResending(true);
    try {
      await session.sendPasswordReset(sentEmail);
    } catch (cause) {
      const err = cause as { code?: string };
      if (err.code === 'auth/too-many-requests') {
        setResendError(t('auth.resetPassword.tooManyAttempts'));
      } else {
        setResendError(t('auth.resetPassword.error'));
      }
    } finally {
      setResending(false);
    }
  }

  const footer = {
    to: AUTH_ROUTE_PATHS.signIn,
    label: t('auth.back'),
    icon: <ArrowLeft aria-hidden className="size-4" />,
  };

  if (sent) {
    return (
      <AuthFrame title={t('auth.resetPassword.heading')} align="center" footer={footer}>
        <div className="flex flex-col gap-4">
          <Alert>
            <AlertDescription>{t('auth.resetPassword.success')}</AlertDescription>
          </Alert>

          <div className="space-y-2">
            <p className="text-sm text-foreground">
              {t('auth.resetPassword.sentTo', { email: sentEmail })}
            </p>
            <p className="text-sm text-muted-foreground">{t('auth.resetPassword.linkExpiry')}</p>
          </div>

          {resendError !== null && (
            <Alert variant="destructive">
              <AlertDescription>{resendError}</AlertDescription>
            </Alert>
          )}

          <Button variant="outline" onClick={() => void handleResend()} disabled={resending}>
            {t('auth.resetPassword.resend')}
          </Button>
        </div>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame title={t('auth.resetPassword.heading')} align="center" footer={footer}>
      <form onSubmit={(event) => void handleSubmit(event)} className="flex flex-col gap-4">
        <Field>
          <FieldLabel htmlFor="reset-password-email">{t('auth.email')}</FieldLabel>
          <Input
            id="reset-password-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        {error !== null && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Button type="submit" disabled={submitting}>
          {t('auth.resetPassword.submit')}
        </Button>
      </form>
    </AuthFrame>
  );
}
